import { produce } from "immer";
import { assertEvent, assign, createActor, emit, setup } from "xstate";
import type { ActorRefFrom, SnapshotFrom } from "xstate";

import { observeSystemPreference } from "@/lib/browser-appearance";
import type { Appearance, SystemPreferenceChangedEvent } from "@/lib/state/appearance";

export interface AppearanceInput {
  readonly initialAppearance: Appearance;
}

export interface AppearanceContext {
  readonly appearance: Appearance;
  readonly systemPrefersDark: boolean;
}

export type AppearanceEvent =
  | { readonly type: "CLIENT_MOUNTED" }
  | SystemPreferenceChangedEvent
  | {
      readonly type: "APPEARANCE_SELECTED";
      readonly appearance: Appearance;
    };

export type AppearanceEmitted =
  | {
      readonly type: "APPEARANCE_PROJECTION_REQUESTED";
      readonly appearance: Appearance;
      readonly systemPrefersDark: boolean;
    }
  | {
      readonly type: "APPEARANCE_PERSISTENCE_REQUESTED";
      readonly appearance: Appearance;
    };

export const appearanceMachine = setup({
  actions: {
    assignSelectedAppearance: assign(({ context, event }) => {
      assertEvent(event, "APPEARANCE_SELECTED");

      return produce(context, (next) => {
        next.appearance = event.appearance;
      });
    }),
    assignSystemPreference: assign(({ context, event }) => {
      assertEvent(event, "SYSTEM_PREFERENCE_CHANGED");

      return produce(context, (next) => {
        next.systemPrefersDark = event.systemPrefersDark;
      });
    }),
    emitSelectedPersistence: emit(({ event }) => {
      assertEvent(event, "APPEARANCE_SELECTED");

      return {
        appearance: event.appearance,
        type: "APPEARANCE_PERSISTENCE_REQUESTED",
      };
    }),
    emitSelectedProjection: emit(({ context, event }) => {
      assertEvent(event, "APPEARANCE_SELECTED");

      return {
        appearance: event.appearance,
        systemPrefersDark: context.systemPrefersDark,
        type: "APPEARANCE_PROJECTION_REQUESTED",
      };
    }),
  },
  actors: { observeSystemPreference },
  guards: {
    appearanceChanged: ({ context, event }) => {
      assertEvent(event, "APPEARANCE_SELECTED");

      return context.appearance !== event.appearance;
    },
  },
  // XState's `types` fields are phantom carriers that enforce this actor's
  // context, input, event, and emitted protocols. The values are erased and
  // never read, so XState provides no assertion-free form for these unions.
  /* oxlint-disable typescript/no-unsafe-type-assertion */
  types: {
    context: {} as AppearanceContext,
    emitted: {} as AppearanceEmitted,
    events: {} as AppearanceEvent,
    input: {} as AppearanceInput,
  },
  /* oxlint-enable typescript/no-unsafe-type-assertion */
}).createMachine({
  context: ({ input }) => ({
    appearance: input.initialAppearance,
    systemPrefersDark: false,
  }),
  id: "appearance",
  initial: "awaitingBrowser",
  on: {
    APPEARANCE_SELECTED: {
      actions: ["assignSelectedAppearance", "emitSelectedProjection", "emitSelectedPersistence"],
      guard: "appearanceChanged",
    },
  },
  states: {
    awaitingBrowser: {
      on: {
        CLIENT_MOUNTED: { target: "observing" },
      },
    },
    observing: {
      invoke: {
        src: "observeSystemPreference",
      },
      on: {
        SYSTEM_PREFERENCE_CHANGED: {
          actions: [
            "assignSystemPreference",
            emit(({ context, event }) => {
              assertEvent(event, "SYSTEM_PREFERENCE_CHANGED");

              return {
                appearance: context.appearance,
                systemPrefersDark: event.systemPrefersDark,
                type: "APPEARANCE_PROJECTION_REQUESTED",
              };
            }),
          ],
        },
      },
    },
  },
});

export type AppearanceActorRef = ActorRefFrom<typeof appearanceMachine>;
export type AppearanceSnapshot = SnapshotFrom<typeof appearanceMachine>;

export const createAppearanceActor = (input: AppearanceInput): AppearanceActorRef =>
  createActor(appearanceMachine, { input });

export const selectAppearance = (snapshot: AppearanceSnapshot): Appearance =>
  snapshot.context.appearance;
